import React from 'react';
import { useTranslation } from 'react-i18next';

const ClusterEfficiencyGauges = ({ summary, loading = false }) => {
  const { t, i18n } = useTranslation();

  const formatCurrency = (amount, currency = 'USD', locale = 'en') => {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency, minimumFractionDigits: 2 }).format(amount || 0);
  };

  const formatPercent = (val) => {
    if (val === undefined || val === null || isNaN(val)) return '0.0%';
    return `${Number(val).toFixed(1)}%`;
  };

  const formatTimestamp = (isoStr) => {
    if (!isoStr) return t('app.na');
    try {
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return isoStr;
      return new Intl.DateTimeFormat(i18n.language || 'en', {
        dateStyle: 'short',
        timeStyle: 'medium'
      }).format(d);
    } catch {
      return isoStr;
    }
  };

  const getEfficiencyColor = (percent) => {
    const p = Number(percent) || 0;
    if (p >= 70) {
      return {
        bg: 'rgba(16, 185, 129, 0.15)',
        border: 'rgba(16, 185, 129, 0.35)',
        text: '#34D399',
        bar: 'linear-gradient(90deg, #059669, #10B981)',
        status: t('finops.telemetry.status_optimal')
      };
    }
    if (p >= 45) {
      return {
        bg: 'rgba(245, 158, 11, 0.15)',
        border: 'rgba(245, 158, 11, 0.35)',
        text: '#FBBF24',
        bar: 'linear-gradient(90deg, #D97706, #F59E0B)',
        status: t('finops.telemetry.status_moderate')
      };
    }
    return {
      bg: 'rgba(239, 68, 68, 0.15)',
      border: 'rgba(239, 68, 68, 0.35)',
      text: '#F87171',
      bar: 'linear-gradient(90deg, #DC2626, #EF4444)',
      status: t('finops.telemetry.status_critical')
    };
  };

  const latestMetrics = summary?.latest_metrics || {};
  const averages = summary?.averages || {};
  const cpuPercent = latestMetrics.cpu_efficiency_percent ?? 0;
  const memPercent = latestMetrics.memory_efficiency_percent ?? 0;
  const avgCpuPercent = averages.cpu_efficiency_percent ?? 0;
  const avgMemPercent = averages.memory_efficiency_percent ?? 0;
  const totalWaste = summary?.total_waste_usd ?? 0;
  const timestamp = latestMetrics.timestamp;

  const cpuColor = getEfficiencyColor(cpuPercent);
  const memColor = getEfficiencyColor(memPercent);

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.65)',
    border: '1px solid rgba(79, 70, 229, 0.28)',
    borderRadius: '16px',
    padding: '20px 24px',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: '140px',
    position: 'relative',
    overflow: 'hidden'
  };

  if (loading) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        {[1, 2, 3, 4].map((n) => (
          <div key={n} style={{ ...cardStyle, justifyContent: 'center', alignItems: 'center' }}>
            <span className="spinner" style={{
              display: 'inline-block',
              width: '24px',
              height: '24px',
              border: '3px solid rgba(79, 70, 229, 0.2)',
              borderTopColor: 'var(--primary-color, #6366F1)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '24px' }}>
      
      {/* CPU Efficiency Gauge Card */}
      <div style={{ ...cardStyle, border: `1px solid ${cpuColor.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <span style={{ fontSize: '0.8rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94A3B8' }}>
              ⚡ {t('finops.telemetry.cpu_efficiency')}
            </span>
            <p style={{ margin: '4px 0 0 0', fontSize: '2.1rem', fontWeight: '800', color: cpuColor.text, letterSpacing: '-0.02em' }}>
              {formatPercent(cpuPercent)}
            </p>
          </div>
          <span style={{
            fontSize: '0.75rem',
            fontWeight: '700',
            padding: '3px 8px',
            borderRadius: '999px',
            background: cpuColor.bg,
            color: cpuColor.text,
            border: `1px solid ${cpuColor.border}`
          }}>
            {cpuColor.status}
          </span>
        </div>

        {/* Progress bar */}
        <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden', margin: '8px 0' }}>
          <div style={{ width: `${Math.min(100, Math.max(0, cpuPercent))}%`, height: '100%', background: cpuColor.bar, transition: 'width 0.8s ease' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94A3B8' }}>
          <span>{t('finops.telemetry.period_avg')}</span>
          <strong style={{ color: '#E2E8F0' }}>{formatPercent(avgCpuPercent)}</strong>
        </div>
      </div>

      {/* Memory Efficiency Gauge Card */}
      <div style={{ ...cardStyle, border: `1px solid ${memColor.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <span style={{ fontSize: '0.8rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94A3B8' }}>
              🧠 {t('finops.telemetry.memory_efficiency')}
            </span>
            <p style={{ margin: '4px 0 0 0', fontSize: '2.1rem', fontWeight: '800', color: memColor.text, letterSpacing: '-0.02em' }}>
              {formatPercent(memPercent)}
            </p>
          </div>
          <span style={{
            fontSize: '0.75rem',
            fontWeight: '700',
            padding: '3px 8px',
            borderRadius: '999px',
            background: memColor.bg,
            color: memColor.text,
            border: `1px solid ${memColor.border}`
          }}>
            {memColor.status}
          </span>
        </div>

        {/* Progress bar */}
        <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden', margin: '8px 0' }}>
          <div style={{ width: `${Math.min(100, Math.max(0, memPercent))}%`, height: '100%', background: memColor.bar, transition: 'width 0.8s ease' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94A3B8' }}>
          <span>{t('finops.telemetry.period_avg')}</span>
          <strong style={{ color: '#E2E8F0' }}>{formatPercent(avgMemPercent)}</strong>
        </div>
      </div>

      {/* Estimated Total Waste Card */}
      <div style={{ ...cardStyle, border: '1px solid rgba(239, 68, 68, 0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <span style={{ fontSize: '0.8rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94A3B8' }}>
              💸 {t('finops.telemetry.estimated_waste')}
            </span>
            <p style={{ margin: '4px 0 0 0', fontSize: '2.1rem', fontWeight: '800', color: totalWaste > 0 ? '#F87171' : '#34D399', letterSpacing: '-0.02em' }}>
              {formatCurrency(totalWaste, 'USD', i18n.language)}
            </p>
          </div>
          <span style={{
            fontSize: '0.75rem',
            fontWeight: '700',
            padding: '3px 8px',
            borderRadius: '999px',
            background: totalWaste > 0 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
            color: totalWaste > 0 ? '#FCA5A5' : '#A7F3D0',
            border: `1px solid ${totalWaste > 0 ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)'}`
          }}>
            {summary?.period === '7d' ? t('finops.telemetry.period_7d') : t('finops.telemetry.period_24h')}
          </span>
        </div>

        <div style={{ marginTop: 'auto', fontSize: '0.75rem', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>⚠️</span>
          <span>{totalWaste > 0 ? t('finops.telemetry.waste_recoverable') : t('finops.telemetry.no_waste_detected')}</span>
        </div>
      </div>

      {/* Latest Telemetry Timestamp Card */}
      <div style={{ ...cardStyle, border: '1px solid rgba(99, 102, 241, 0.3)' }}>
        <div>
          <span style={{ fontSize: '0.8rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94A3B8' }}>
            ⏱️ {t('finops.telemetry.latest_sample')}
          </span>
          <p style={{ margin: '8px 0 4px 0', fontSize: '1.2rem', fontWeight: '700', color: '#E2E8F0', letterSpacing: '-0.01em' }}>
            {formatTimestamp(timestamp)}
          </p>
        </div>

        <div style={{ marginTop: 'auto', fontSize: '0.75rem', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981', display: 'inline-block' }}></span>
          <span>{summary?.cluster_id || t('finops.telemetry.cluster_default')} (RFC3339)</span>
        </div>
      </div>

    </div>
  );
};

export default ClusterEfficiencyGauges;
